// hooks/useGlobalShortcuts.ts
/**
 * App-level keyboard shortcuts.
 *
 * Extracted from AuthenticatedApp (F3). The hook owns only the key matching
 * and the "is the user typing?" guard; what each shortcut does stays with the
 * component that owns the state.
 */

import { useEffect, useRef } from 'react';

export interface UseGlobalShortcutsParams {
  /** ⌘/Ctrl+K — admin command center. Ignored for non-admins. */
  isAdmin: boolean;
  onOpenCommandPalette: () => void;
  /** "/" — student command bar. Suppressed while typing in a field. */
  onOpenCommandBar: () => void;
  /** ⌘/Ctrl+Shift+O — rotate the optimizer mode. */
  onRotateOptimizerMode: () => void;
}

/** True when the event originated inside an editable field. */
const isEditableTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  return (
    !!element &&
    (element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName))
  );
};

export function useGlobalShortcuts({
  isAdmin,
  onOpenCommandPalette,
  onOpenCommandBar,
  onRotateOptimizerMode,
}: UseGlobalShortcutsParams): void {
  // Handlers live in a ref so the listener is bound once and callers don't
  // need to memoize every callback.
  const handlersRef = useRef({ onOpenCommandPalette, onOpenCommandBar, onRotateOptimizerMode });
  handlersRef.current = { onOpenCommandPalette, onOpenCommandBar, onRotateOptimizerMode };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K: Command Center (admin only)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k' && isAdmin) {
        e.preventDefault();
        handlersRef.current.onOpenCommandPalette();
      }
      // "/": student command bar — only outside text fields, no modifiers
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (!isEditableTarget(e.target)) {
          e.preventDefault();
          handlersRef.current.onOpenCommandBar();
        }
      }
      // Cmd+Shift+O: rotate optimizer mode (v4 is default) — ADMIN ONLY.
      // This is a dev affordance: the mode is not persisted and no UI offers
      // it, but until 2026-08-23 any logged-in student could press it and put
      // themselves on a non-default engine for that page session. Gated for
      // the same reason Cmd+K is.
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'o' || e.key === 'O') && isAdmin) {
        e.preventDefault();
        handlersRef.current.onRotateOptimizerMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAdmin]);
}
